import { Salutation } from '@schuelerhilfe/shared/src/enums/salutation';
import { z } from 'zod';

const noAt = (v: string) => !v.includes('@');

/**
 * One CMS-managed message per field, replacing every default message of
 * that field (required, format, no-@) — the same one-text-per-field
 * simplification the Adjustable Contact Form makes. Absent entries keep
 * the `next-intl` defaults.
 */
export type FormMessageOverrides = Partial<
  Record<
    | 'salutation'
    | 'firstname'
    | 'lastname'
    | 'phone'
    | 'email'
    | 'message'
    | 'consent',
    string
  >
>;

export function createFormSchemas(
  t: (key: string) => string,
  overrides: FormMessageOverrides = {},
) {
  const msg = (field: keyof FormMessageOverrides, key: string) =>
    overrides[field] ?? t(key);

  return {
    salutationSchema: z.enum(Salutation, {
      message: msg('salutation', 'salutationRequired'),
    }),

    firstnameSchema: z
      .string()
      .trim()
      .min(1, msg('firstname', 'firstnameRequired'))
      .refine(noAt, msg('firstname', 'noAtSymbol')),

    lastnameSchema: z
      .string()
      .trim()
      .min(1, msg('lastname', 'lastnameRequired'))
      .refine(noAt, msg('lastname', 'noAtSymbol')),

    phoneSchema: z
      .string()
      .trim()
      .min(1, msg('phone', 'phoneRequired'))
      .refine(noAt, msg('phone', 'noAtSymbol'))
      .refine(
        (v) => /^\+?\d{7,15}$/.test(v.replace(/[\s\-/()]/g, '')),
        msg('phone', 'phoneInvalid'),
      ),

    emailSchema: z
      .string()
      .trim()
      .min(1, msg('email', 'emailRequired'))
      .pipe(z.email(msg('email', 'emailInvalid'))),

    messageSchema: z
      .string()
      .trim()
      .min(1, msg('message', 'messageRequired'))
      .refine(noAt, msg('message', 'noAtSymbol')),

    consentSchema: z.literal(true, {
      message: msg('consent', 'consentRequired'),
    }),
  };
}
